import { z } from 'zod';

import type { MlsConfigCreate, MlsConfigFields } from '~/modules/query/mls';

/** The form's copy of a row: strings throughout, so an input never holds a number or null. */
export type MlsConfigDraft = Record<keyof MlsConfigFields, string>;

type CodeField =
  | 'feedType'
  | 'listTracEnabled'
  | 'listingAttrType'
  | 'listingAttrLocation'
  | 'showOffMarketListings';

export const CODE_OPTIONS: Record<CodeField, { value: string; label: string }[]> = {
  feedType: [
    { value: 'RETS', label: 'RETS' },
    { value: 'WebAPI', label: 'WebAPI' },
    { value: 'API', label: 'API' },
  ],
  listTracEnabled: [
    { value: 'F', label: 'No' },
    { value: 'T', label: 'Yes' },
  ],
  listingAttrType: [
    { value: 'DEFAULT', label: 'Default' },
    { value: 'CUSTOM', label: 'Custom' },
  ],
  listingAttrLocation: [
    { value: '1', label: 'Above "Request Showing"' },
    { value: '2', label: 'Top Right Side Bar' },
  ],
  showOffMarketListings: [
    { value: 'T', label: 'Yes' },
    { value: 'F', label: 'No' },
  ],
};

const CODE_FIELDS = Object.keys(CODE_OPTIONS) as CodeField[];
const isCodeField = (field: string): field is CodeField => field in CODE_OPTIONS;

/** A stored code as its canonical option value, matched case-insensitively; '' ("Not set") for NULL, empty or unknown. */
export function normalizeCode(field: CodeField, stored: string | null | undefined): string {
  const wanted = (stored ?? '').trim().toLowerCase();
  return CODE_OPTIONS[field].find((option) => option.value.toLowerCase() === wanted)?.value ?? '';
}

/** The row a new feed starts from (PRD requirement 3). */
export function createDefaults(mlsid: string): MlsConfigCreate {
  return {
    mlsid,
    description: '',
    contact: '',
    phone: '',
    feedType: null,
    userName: '',
    password: '',
    feedUrl: '',
    rowLimit: 0,
    listTracEnabled: 'F',
    disclaimer: '',
    imagePath: '',
    imageUrl: '',
    attachmentPath: '',
    listingAttrType: 'CUSTOM',
    listingAttrLocation: '1',
    listingAttrContent: '',
    showOffMarketListings: 'T',
  };
}

export function toDraft(config: MlsConfigFields): MlsConfigDraft {
  const text = (value: string | number | null | undefined) => (value ?? '').toString();
  const draft = {
    description: text(config.description),
    contact: text(config.contact),
    phone: text(config.phone),
    feedType: '',
    userName: text(config.userName),
    password: text(config.password),
    feedUrl: text(config.feedUrl),
    rowLimit: text(config.rowLimit),
    listTracEnabled: '',
    disclaimer: text(config.disclaimer),
    imagePath: text(config.imagePath),
    imageUrl: text(config.imageUrl),
    attachmentPath: text(config.attachmentPath),
    listingAttrType: '',
    listingAttrLocation: '',
    listingAttrContent: text(config.listingAttrContent),
    showOffMarketListings: '',
  } satisfies MlsConfigDraft;
  for (const field of CODE_FIELDS) draft[field] = normalizeCode(field, config[field]);
  return draft;
}

/** One draft value as the API takes it: a number for the row limit, null for an unset code, else the text. */
function toValue(field: keyof MlsConfigFields, value: string): string | number | null {
  if (field === 'rowLimit') return Number(value);
  if (isCodeField(field)) return value === '' ? null : value;
  return value;
}

/**
 * The fields the operator changed, and only those. Untouched HTML stays byte-identical, a NULL code
 * stays NULL, and a column the form never shows is never written.
 */
export function changedFields(
  loaded: MlsConfigDraft,
  draft: MlsConfigDraft,
): Partial<MlsConfigFields> {
  const changed: Record<string, string | number | null> = {};
  for (const field of Object.keys(draft) as (keyof MlsConfigFields)[]) {
    if (draft[field] !== loaded[field]) changed[field] = toValue(field, draft[field]);
  }
  return changed as Partial<MlsConfigFields>;
}

/** The whole draft as a create body. */
export function toCreateBody(mlsid: string, draft: MlsConfigDraft): MlsConfigCreate {
  const body: Record<string, string | number | null> = { mlsid };
  for (const field of Object.keys(draft) as (keyof MlsConfigFields)[]) {
    body[field] = toValue(field, draft[field]);
  }
  return body as unknown as MlsConfigCreate;
}

const text = (label: string, limit: number) =>
  z.string().max(limit, `${label} can be at most ${limit} characters.`);

const path = (label: string) =>
  text(label, 128).refine(
    (value) => !value.includes('|'),
    `${label} can't contain a "|". The part after it is kept as stored.`,
  );

export const mlsConfigSchema = z.object({
  description: text('Description', 64),
  contact: text('Contact', 64),
  phone: text('Phone', 64),
  userName: text('User name', 64),
  password: text('Password', 128),
  feedUrl: text('Feed URL', 128),
  rowLimit: z
    .string()
    .refine(
      (value) => /^\d+$/.test(value) && Number(value) <= 999999,
      'Row limit must be a whole number from 0 to 999999.',
    ),
  imagePath: path('Images absolute path'),
  imageUrl: path('Images URL'),
  attachmentPath: path('Attachments absolute path'),
});

const LISTING_PLACEHOLDERS = [
  { value: '{{MLSID}}', label: 'MLS ID' },
  { value: '{{LO_Name}}', label: 'Listing office name' },
  { value: '{{LO_Email}}', label: 'Listing office email' },
  { value: '{{LO_Phone}}', label: 'Listing office phone' },
];

const FIRM_PLACEHOLDERS = [
  { value: '{{FirmName}}', label: 'Firm name' },
  { value: '{{AttributionTypeListing}}', label: 'Attribution type' },
];

const AGENT_PLACEHOLDERS = [
  { value: '{{LA_Name}}', label: 'Listing agent name' },
  { value: '{{LA_Email}}', label: 'Listing agent email' },
  { value: '{{LA_Phone}}', label: 'Listing agent phone' },
];

const FIRM_FEEDS = new Set(['mls_canopy', 'mls_neren']);

/** The fields the custom attribution picker offers for this feed. */
export function attributionPlaceholders(mlsid: string): { value: string; label: string }[] {
  const firm = FIRM_FEEDS.has(mlsid.trim().toLowerCase());
  return [...LISTING_PLACEHOLDERS, ...(firm ? FIRM_PLACEHOLDERS : AGENT_PLACEHOLDERS)];
}

/** The sample shown while Attribution fields is Default or Not set. */
export const DEFAULT_ATTRIBUTION_PREVIEW = [
  'MLS #: 123456789',
  'Listing provided by: Jane Smith - (999) 999-9999',
  'Firm Name LLC - (999) 999-9999 - email@email.com',
];
